import { useId, useState } from 'react';

import { CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetFreezeStorageLocationsQuery } from '@/app/services/locations';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
} from '@/components/ui/popover';
import { useEmbryoSummaryReport } from '@/services/reports';

import { getFertilizationSiteId } from '../utils/utils';

type StorageLocationOption = { id: string; name: string };

type EmbryoSummaryPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string | null;
  siteId: string | null;
  siteName: string;
  anchorRef: React.RefObject<HTMLElement | null>;
};

export function EmbryoSummaryPopover({
  open,
  onOpenChange,
  date,
  siteId,
  siteName,
  anchorRef,
}: EmbryoSummaryPopoverProps) {
  const { t } = useTranslation();
  const dateId = useId();
  const siteFieldId = useId();
  const storageLocationId = useId();

  const [embryoSummary] = useEmbryoSummaryReport();
  const [selectedLocation, setSelectedLocation] =
    useState<StorageLocationOption | null>(null);

  const { data } = useGetFreezeStorageLocationsQuery(
    getFertilizationSiteId(siteId),
    {
      skip: !siteId,
    },
  );
  const namedLocations = data?.filter(
    (loc) => loc.name,
  ) as StorageLocationOption[];

  const handleConfirm = async () => {
    if (!date || !siteId) {
      return;
    }

    try {
      onOpenChange(false);
      await embryoSummary({
        fertilizationSiteId: siteId,
        fertilizationDate: date,
        storageLocationId: selectedLocation?.id,
      });
    } catch (error) {
      onOpenChange(true);
      console.error('Error generating embryo summary:', error);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setSelectedLocation(null);
        onOpenChange(nextOpen);
      }}
    >
      <PopoverContent align="end" anchor={anchorRef} className="w-96">
        <PopoverHeader>
          <PopoverTitle>{t('embryo.embryoSummary')}</PopoverTitle>
        </PopoverHeader>
        <FieldGroup className="gap-4">
          <Field orientation="horizontal">
            <FieldLabel htmlFor={dateId} className="w-1/2">
              {t('common.date')}
            </FieldLabel>
            <Input id={dateId} disabled value={date ?? ''} />
          </Field>
          <Field orientation="horizontal">
            <FieldLabel htmlFor={siteFieldId} className="w-1/2">
              {t('site.site')}
            </FieldLabel>
            <Input id={siteFieldId} disabled value={siteName} />
          </Field>
          <Field orientation="horizontal">
            <FieldLabel htmlFor={storageLocationId} className="w-1/2">
              {t('storageLocation.storageLocation')}
            </FieldLabel>
            <Combobox<StorageLocationOption>
              items={namedLocations}
              value={selectedLocation}
              onValueChange={setSelectedLocation}
              itemToStringValue={(option) => option.id}
              itemToStringLabel={(option) => option.name}
            >
              <ComboboxInput
                id={storageLocationId}
                className="w-full"
                placeholder={t('common.all')}
                showClear
              />
              <ComboboxContent>
                <ComboboxEmpty>{t('common.noItemsFound')}</ComboboxEmpty>
                <ComboboxList>
                  {(option: StorageLocationOption) => (
                    <ComboboxItem key={option.id} value={option}>
                      {option.name}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
        </FieldGroup>
        <Button className="mt-2" onClick={handleConfirm}>
          <CheckIcon className="mr-2 size-4" /> {t('common.confirm')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
